import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { TrendingUp, TrendingDown, Minus, Plus, Pencil, Trash2, CopyCheck, GripVertical, X, Sparkles, RefreshCw, Star } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { authSelector } from '../features/auth/slice';

const Chart = lazy(() => import('react-apexcharts'));

const CATEGORIES = ['인건비', '임대료', '서버/IT', '마케팅', '수수료', '세금/4대보험', '운영비', '기타'];

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

function pctChange(curr, prev) {
  if (!prev) return null;
  return (((curr - prev) / prev) * 100).toFixed(1);
}

function ChangeBadge({ current, prev }) {
  const p = pctChange(current, prev);
  if (p === null) return null;
  const up = p > 0, zero = p == 0;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
      zero ? 'bg-white/5 text-slate-400'
      : up ? 'bg-sky-500/10 text-sky-400'
      : 'bg-rose-500/10 text-rose-400'}`}>
      {up && '▲ '}{!up && !zero && '▼ '}{zero ? '-' : `${up ? '+' : ''}${p}%`}
    </span>
  );
}

export default function RevenueAnalysisPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();

  useEffect(() => {
    if (me && me.superAdminYn !== 'Y') navigate('/dashboard', { replace: true });
  }, [me, navigate]);

  const [monthList, setMonthList] = useState([]);
  const [month, setMonth] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [monthListLoading, setMonthListLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pendingRows, setPendingRows] = useState([]);
  const [inheritLoading, setInheritLoading] = useState(false);
  const [localExpenses, setLocalExpenses] = useState([]);
  const [expenseSortMode, setExpenseSortMode] = useState('amount'); // 'amount' | 'category' | 'custom'
  const dragIndexRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const [aiReport, setAiReport] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const aiAbortRef = useRef(null);

  // 월 리스트
  useEffect(() => {
    if (!me) return;
    setMonthListLoading(true);
    axios.get('/ByClient/getMonthList')
      .then((res) => {
        const list = res.data || [];
        setMonthList(list);
        if (list.length > 0) setMonth(list[0]);
      })
      .finally(() => setMonthListLoading(false));
  }, [me]);

  useEffect(() => {
    setCurrentIndex(monthList.indexOf(month));
  }, [month, monthList]);

  const fetchSummary = useCallback(async (m) => {
    if (!m) return;
    setLoading(true);
    try {
      const [sumRes, trendRes] = await Promise.all([
        axios.get('/api/revenue-analysis/summary', { params: { calculateMonth: m } }),
        axios.get('/api/revenue-analysis/trend', { params: { months: 12 } }),
      ]);
      setSummary(sumRes.data);
      setTrend(trendRes.data);
    } catch (e) {
      console.error('수익분석 조회 오류:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const generateAiReport = useCallback(async (m, force = false) => {
    if (!m) return;

    // DB 캐시 확인 (강제 재생성 아닐 때)
    if (!force) {
      try {
        const res = await axios.get('/api/revenue-analysis/ai-report-cache', { params: { calculateMonth: m } });
        if (res.data?.report) {
          setAiReport(res.data.report);
          return;
        }
      } catch {}
    }

    if (aiAbortRef.current) aiAbortRef.current.abort();
    const controller = new AbortController();
    aiAbortRef.current = controller;
    setAiLoading(true);
    setAiReport('');

    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${apiBase}/api/revenue-analysis/ai-report?calculateMonth=${m}`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.text) setAiReport((prev) => prev + parsed.text);
          } catch {}
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') setAiReport('분석 생성 중 오류가 발생했습니다.');
    } finally {
      setAiLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!me || !month) return;
    fetchSummary(month);
    setAiReport('');
    generateAiReport(month, false);
  }, [me, month, fetchSummary, generateAiReport]);

  const handlePrevMonth = () => {
    if (currentIndex < monthList.length - 1) setMonth(monthList[currentIndex + 1]);
  };
  const handleNextMonth = () => {
    if (currentIndex > 0) setMonth(monthList[currentIndex - 1]);
  };

  const formatTitle = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    return `${y}년 ${parseInt(mo, 10)}월 수익분석`;
  };

  // 고정지출 CRUD
  const handleBulkSave = async () => {
    const valid = pendingRows.filter((r) => r.label && r.amount);
    if (valid.length === 0) return;
    await axios.post('/api/revenue-analysis/fixed-expenses/bulk', { calculateMonth: month, items: valid });
    setPendingRows([]);
    fetchSummary(month);
  };

  const handleDrop = async (dropIndex) => {
    const fromIndex = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverIndex(null);
    setIsDragging(false);
    if (fromIndex === null || fromIndex === dropIndex) return;
    const reordered = [...localExpenses];
    const [item] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, item);
    setLocalExpenses(reordered);
    await axios.put('/api/revenue-analysis/fixed-expenses/reorder', { ids: reordered.map((e) => e.id) });
  };

  const handleToggleHighlight = async (id) => {
    setLocalExpenses((prev) =>
      prev.map((e) => e.id === id ? { ...e, highlighted: !e.highlighted } : e)
    );
    try {
      await axios.put(`/api/revenue-analysis/fixed-expenses/${id}/highlight`);
    } catch {
      setLocalExpenses((prev) =>
        prev.map((e) => e.id === id ? { ...e, highlighted: !e.highlighted } : e)
      );
    }
  };

  const handleEdit = async (id) => {
    await axios.put(`/api/revenue-analysis/fixed-expenses/${id}`, editForm);
    setEditingId(null);
    fetchSummary(month);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await axios.delete(`/api/revenue-analysis/fixed-expenses/${id}`);
    fetchSummary(month);
  };

  const handleInherit = async () => {
    if (!window.confirm('전월 고정지출을 이번 달로 가져오시겠습니까? 기존 항목은 모두 삭제됩니다.')) return;
    setInheritLoading(true);
    try {
      await axios.post('/api/revenue-analysis/fixed-expenses/inherit', { calculateMonth: month });
      fetchSummary(month);
    } catch (e) {
      alert(e.response?.data?.errorMessage || '전월 데이터가 없습니다.');
    } finally {
      setInheritLoading(false);
    }
  };

  const c = summary?.current;
  const p = summary?.prev;
  const expenses = summary?.fixedExpenses || [];

  useEffect(() => { setLocalExpenses(summary?.fixedExpenses || []); }, [summary]);

  const chartOptions = {
    chart: { type: 'bar', background: 'transparent', toolbar: { show: false }, fontFamily: 'inherit' },
    colors: ['#6366f1', '#10b981', '#f43f5e'],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: trend.map((t) => {
        const [y, m] = t.calculateMonth.split('-');
        return `${y.slice(2)}.${parseInt(m)}`;
      }),
      labels: { style: { colors: '#64748b', fontSize: '11px' } },
    },
    yaxis: { labels: { style: { colors: '#64748b', fontSize: '11px' }, formatter: (v) => `${(v / 10000).toFixed(0)}만` } },
    legend: { labels: { colors: '#94a3b8' }, position: 'top', fontSize: '12px' },
    grid: { borderColor: 'rgba(255,255,255,0.05)' },
    tooltip: { theme: 'dark', y: { formatter: (v) => `${Number(v).toLocaleString()}원` } },
  };

  const chartSeries = [
    { name: '순수수료수익', data: trend.map((t) => t.feeRevenue - t.commissionRevenue) },
    { name: '영업이익', data: trend.map((t) => t.operatingProfit) },
    { name: '고정지출', data: trend.map((t) => t.totalFixed) },
  ];

  return (
    <>
      {monthListLoading || !month ? (
        <div className="w-full h-[calc(100vh-96px)] flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mb-4" />
          <div className="text-slate-400 text-sm">수익분석 초기화 중...</div>
        </div>
      ) : (
        <>
          {/* 월 헤더 */}
          <div className="flex mb-8 justify-center items-center gap-5">
            <button
              onClick={handlePrevMonth}
              disabled={currentIndex === monthList.length - 1}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              {formatTitle(month)}
            </h1>
            <button
              onClick={handleNextMonth}
              disabled={currentIndex === 0}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 gap-3 text-slate-400 text-sm">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-sky-500" />
              불러오는 중...
            </div>
          ) : c ? (
            <div className="space-y-6">
              {/* 요약 카드 5개 한 줄 */}
              <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
                {[
                  { label: '플랫폼 총매출', value: c.totalRevenue, prev: p?.totalRevenue, sub: `${c.clientCount}개 권리사`, accent: '' },
                  { label: '순수수료수익', value: c.netFeeRevenue, prev: p?.netFeeRevenue, sub: `유통 ${fmt(c.feeRevenue)}\n- 별도 ${fmt(c.commissionRevenue)}`, accent: 'text-sky-400' },
                  { label: '사업자 지급액', value: c.businessPaidAmount, prev: p?.businessPaidAmount, sub: `${c.businessCount}개사`, accent: '' },
                  { label: '개인 지급액', value: c.personalPaidAmount, prev: p?.personalPaidAmount, sub: `${c.personalCount}명`, accent: '' },
                  { label: '이월액', value: c.carriedAmount, prev: p?.carriedAmount, sub: `전월이월 ${fmt(c.prevCarried)}원`, accent: 'text-amber-400' },
                ].map(({ label, value, prev, sub, accent }) => (
                  <div key={label} className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">{label}</span>
                    </div>
                    <div className="flex flex-col justify-center items-center h-28 px-5 gap-1">
                      <div className="flex items-baseline gap-1">
                        <span className={`text-2xl font-bold ${accent || 'text-white'}`}>{fmt(value)}</span>
                        <span className="text-sm font-medium text-slate-500">원</span>
                      </div>
                      {sub && <div className="text-xs text-slate-400 text-center whitespace-pre-line leading-4">{sub}</div>}
                      {prev !== undefined && <ChangeBadge current={value} prev={prev} />}
                    </div>
                  </div>
                ))}
              </div>

              {/* 손익계산서 */}
              <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">손익계산서</span>
                </div>

                {/* 수익 항목 */}
                <div className="divide-y divide-white/[0.04]">
                  {/* 영업이익 */}
                  <div className={`px-5 py-4 flex items-center justify-between ${c.operatingProfit >= 0 ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`}>
                    <span className="text-sm font-bold text-white">영업이익</span>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-2xl font-bold ${c.operatingProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {c.operatingProfit >= 0 ? '+' : ''}{fmt(c.operatingProfit)}
                      </span>
                      <span className="text-sm text-slate-400">원</span>
                    </div>
                  </div>

                  <PlRow label="순수수료수익" value={c.netFeeRevenue} bold accent="text-sky-300" />

                  {/* 고정지출 헤더 */}
                  <div className="px-5 py-3 flex items-center justify-between bg-white/[0.02]">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-slate-300">고정지출</span>
                      {[
                        { key: 'amount', label: '높은금액순' },
                        { key: 'category', label: '분류별' },
                        { key: 'custom', label: '수동 정렬' },
                      ].map(({ key, label }) => (
                        <button key={key}
                          onClick={() => setExpenseSortMode(key)}
                          className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${expenseSortMode === key ? 'border-sky-500/60 text-sky-400 bg-sky-500/10' : 'border-white/10 text-slate-500 hover:text-slate-300'}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <span className="text-base font-bold text-rose-400">- {fmt(c.totalFixed)}원</span>
                  </div>

                  {/* 고정지출 항목들 */}
                  {(expenseSortMode === 'category'
                    ? [...localExpenses].sort((a, b) => a.category.localeCompare(b.category))
                    : expenseSortMode === 'amount'
                      ? [...localExpenses].sort((a, b) => Number(b.amount) - Number(a.amount))
                      : localExpenses
                  ).map((e, idx) => (
                    <div
                      key={e.id}
                      draggable={expenseSortMode === 'custom'}
                      onDragStart={() => { dragIndexRef.current = idx; setIsDragging(true); }}
                      onDragOver={(ev) => { ev.preventDefault(); setDragOverIndex(idx); }}
                      onDragLeave={() => setDragOverIndex(null)}
                      onDrop={() => handleDrop(idx)}
                      onDragEnd={() => { dragIndexRef.current = null; setDragOverIndex(null); setIsDragging(false); }}
                      className={`px-5 py-2.5 flex items-center gap-3 transition-colors border-b border-white/[0.03] last:border-0
                        ${e.highlighted ? 'bg-amber-500/10' : ''}
                        ${dragOverIndex === idx && expenseSortMode === 'custom' ? 'bg-sky-500/10 border-t border-sky-500/40' : ''}
                        ${expenseSortMode === 'custom' && !isDragging ? 'hover:bg-white/[0.02]' : ''}`}>
                      {expenseSortMode === 'custom'
                        ? <GripVertical size={14} className="text-slate-600 shrink-0 cursor-grab active:cursor-grabbing" />
                        : <div className="w-[14px] shrink-0" />}
                      {editingId === e.id ? (
                        <>
                          <select value={editForm.category}
                            onChange={(ev) => setEditForm((pv) => ({ ...pv, category: ev.target.value }))}
                            className="text-xs bg-[#0d1526] text-white rounded-lg px-2 py-1.5 border border-white/10">
                            {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
                          </select>
                          <input value={editForm.label}
                            onChange={(ev) => setEditForm((pv) => ({ ...pv, label: ev.target.value }))}
                            className="flex-1 text-xs bg-[#0d1526] text-white rounded-lg px-2 py-1.5 border border-white/10" placeholder="항목명" />
                          <input type="number" value={editForm.amount}
                            onChange={(ev) => setEditForm((pv) => ({ ...pv, amount: ev.target.value }))}
                            className="w-28 text-xs bg-[#0d1526] text-white rounded-lg px-2 py-1.5 border border-white/10 text-right" placeholder="금액" />
                          <button onClick={() => handleEdit(e.id)} className="text-xs text-sky-400 hover:text-sky-200 font-medium">저장</button>
                          <button onClick={() => setEditingId(null)} className="text-xs text-slate-400 hover:text-white">취소</button>
                        </>
                      ) : (
                        <>
                          <span className="text-[10px] text-slate-500 bg-white/5 px-1.5 py-0.5 rounded shrink-0">{e.category}</span>
                          <span className={`flex-1 text-xs ${e.highlighted ? 'text-amber-200 font-medium' : 'text-slate-300'}`}>{e.label}</span>
                          <span className="text-xs text-rose-300 font-medium shrink-0">{fmt(e.amount)}원</span>
                          <button
                            onClick={() => handleToggleHighlight(e.id)}
                            title={e.highlighted ? '하이라이트 해제' : '하이라이트'}
                            className={`transition-colors shrink-0 ${e.highlighted ? 'text-amber-400 hover:text-amber-300' : 'text-slate-600 hover:text-amber-400'}`}>
                            <Star size={12} fill={e.highlighted ? 'currentColor' : 'none'} />
                          </button>
                          <button onClick={() => { setEditingId(e.id); setEditForm({ category: e.category, label: e.label, amount: e.amount }); }}
                            className="text-slate-500 hover:text-slate-300 transition-colors shrink-0"><Pencil size={12} /></button>
                          <button onClick={() => handleDelete(e.id)}
                            className="text-slate-500 hover:text-rose-400 transition-colors shrink-0"><Trash2 size={12} /></button>
                        </>
                      )}
                    </div>
                  ))}

                  {/* 추가 대기 행들 */}
                  {pendingRows.map((row, idx) => (
                    <div key={idx} className="px-5 py-2.5 flex items-center gap-3 bg-sky-500/5 border-t border-sky-500/10">
                      <div className="w-[14px] shrink-0" />
                      <select value={row.category}
                        onChange={(ev) => setPendingRows((pv) => pv.map((r, i) => i === idx ? { ...r, category: ev.target.value } : r))}
                        className="text-xs bg-[#0d1526] text-white rounded-lg px-2 py-1.5 border border-white/10">
                        {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
                      </select>
                      <input value={row.label}
                        onChange={(ev) => setPendingRows((pv) => pv.map((r, i) => i === idx ? { ...r, label: ev.target.value } : r))}
                        className="flex-1 text-xs bg-[#0d1526] text-white rounded-lg px-2 py-1.5 border border-white/10" placeholder="항목명" />
                      <input type="number" value={row.amount}
                        onChange={(ev) => setPendingRows((pv) => pv.map((r, i) => i === idx ? { ...r, amount: ev.target.value } : r))}
                        className="w-28 text-xs bg-[#0d1526] text-white rounded-lg px-2 py-1.5 border border-white/10 text-right" placeholder="금액" />
                      <button onClick={() => setPendingRows((pv) => pv.filter((_, i) => i !== idx))}
                        className="text-slate-500 hover:text-rose-400 transition-colors shrink-0"><X size={13} /></button>
                    </div>
                  ))}

                  {/* 하단 액션 */}
                  <div className="px-5 py-2.5 flex items-center gap-4 border-t border-white/[0.04]">
                    <button onClick={() => setPendingRows((pv) => [...pv, { category: '운영비', label: '', amount: '' }])}
                      className="flex items-center gap-1 text-xs text-slate-500 hover:text-sky-400 transition-colors">
                      <Plus size={13} /> 행 추가
                    </button>
                    <button onClick={handleInherit} disabled={inheritLoading}
                      className="flex items-center gap-1 text-xs text-slate-500 hover:text-amber-400 transition-colors disabled:opacity-50">
                      <CopyCheck size={13} /> {inheritLoading ? '처리 중...' : '전월 계승'}
                    </button>
                    {pendingRows.length > 0 && (
                      <>
                        <button onClick={handleBulkSave}
                          className="text-xs text-sky-400 hover:text-sky-200 font-medium transition-colors">
                          등록 ({pendingRows.length})
                        </button>
                        <button onClick={() => setPendingRows([])}
                          className="text-xs text-slate-500 hover:text-white transition-colors">
                          취소
                        </button>
                      </>
                    )}
                  </div>

                </div>
              </div>

              {/* AI 분석 리포트 */}
              <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.05]">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-violet-400" />
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">AI 수익 분석</span>
                  </div>
                  <button
                    onClick={() => generateAiReport(month, true)}
                    disabled={aiLoading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-violet-500/15 text-violet-400 hover:bg-violet-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium">
                    {aiLoading
                      ? <><RefreshCw size={12} className="animate-spin" /> 분석 중...</>
                      : <><RefreshCw size={12} /> 새로고침</>}
                  </button>
                </div>
                <div className="px-5 py-4 min-h-[80px]">
                  {!aiReport && !aiLoading && (
                    <p className="text-xs text-slate-500 leading-relaxed">
                      분석 데이터를 준비 중입니다...
                    </p>
                  )}
                  {(aiReport || aiLoading) && (
                    <div className="text-sm text-slate-200 leading-7 whitespace-pre-wrap">
                      {aiReport}
                      {aiLoading && (
                        <span className="inline-block w-1.5 h-4 bg-violet-400 ml-0.5 animate-pulse rounded-sm align-middle" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 트렌드 차트 */}
              {trend.length > 1 && (
                <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
                  <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">월별 수익 추이 (최근 12개월)</span>
                  </div>
                  <div className="p-4">
                    <Suspense fallback={<div className="h-52 flex items-center justify-center text-slate-400 text-sm">차트 로딩 중...</div>}>
                      <Chart options={chartOptions} series={chartSeries} type="bar" height={220} />
                    </Suspense>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

function PlRow({ label, value, bold, accent, indent, negative }) {
  return (
    <div className={`px-5 py-3 flex items-center justify-between ${indent ? 'pl-10' : ''}`}>
      <span className={`${bold ? 'text-sm font-bold text-slate-200' : 'text-xs text-slate-500'}`}>{label}</span>
      <span className={`${bold ? 'text-base font-bold' : 'text-sm font-medium text-slate-500'} ${accent || ''}`}>
        {negative ? '- ' : ''}{fmt(value)}원
      </span>
    </div>
  );
}
